import React, { useState } from 'react';
import '../styles/Invoices.css';

function InvoiceForm({ customers, products, onSave, onClose, onAddCustomer, invoiceType = 'with-gst' }) {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [items, setItems] = useState([]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [roundOff, setRoundOff] = useState(false);
  const [invoiceState, setInvoiceState] = useState('Karnataka');

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    quantity: 1,
    discount: 0,
    mrp: 0,
  });

  const handleAddItem = () => {
    if (!currentItem.productId || currentItem.quantity <= 0) {
      alert('Please select a product and enter quantity');
      return;
    }

    const product = products.find((p) => p.id === currentItem.productId);
    if (!product) return;

    // Use MRP from product or from input
    const mrp = currentItem.mrp || product.mrp || product.rate || 0;
    
    // Calculate Rate from MRP by removing tax
    // Formula: Rate = MRP / (1 + TaxRate/100)
    let rate = mrp;
    let cgst = 0, sgst = 0, igst = 0;
    
    if (invoiceType === 'with-gst' && product.taxRate > 0) {
      // Rate = MRP / (1 + TaxRate/100)
      rate = mrp / (1 + product.taxRate / 100);
      
      // Tax = MRP - Rate
      const totalTax = mrp - rate;
      
      if (selectedCustomer) {
        const customer = customers.find((c) => c.id === selectedCustomer);
        // Split tax into CGST/SGST
        cgst = (totalTax / 2) * currentItem.quantity;
        sgst = (totalTax / 2) * currentItem.quantity;
      }
    }

    // Taxable amount = Rate * Quantity
    const taxableAmount = rate * currentItem.quantity;
    
    // Apply discount on taxable amount
    const discountAmount = (taxableAmount * currentItem.discount) / 100;
    const finalTaxableAmount = taxableAmount - discountAmount;
    
    // Recalculate tax on discounted amount
    if (currentItem.discount > 0 && invoiceType === 'with-gst' && product.taxRate > 0) {
      const taxOnDiscounted = (finalTaxableAmount * product.taxRate) / 100;
      cgst = taxOnDiscounted / 2;
      sgst = taxOnDiscounted / 2;
    }

    const newItem = {
      productId: product.id,
      productName: product.productName,
      hsnCode: product.hsnCode,
      quantity: currentItem.quantity,
      rate: rate,
      mrp: mrp,
      discount: currentItem.discount,
      taxRate: invoiceType === 'with-gst' ? product.taxRate : 0,
      cgst,
      sgst,
      igst,
      itemTotal: finalTaxableAmount + cgst + sgst + igst,
    };

    setItems([...items, newItem]);
    setCurrentItem({ productId: '', quantity: 1, discount: 0, mrp: 0 });
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      // Taxable amount = Rate * Quantity
      const taxableAmount = item.rate * item.quantity;
      // Apply discount
      const discountAmount = (taxableAmount * item.discount) / 100;
      const finalTaxableAmount = taxableAmount - discountAmount;
      
      subtotal += finalTaxableAmount;
      totalTax += item.cgst + item.sgst + item.igst;
    });

    return { subtotal, totalTax };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedCustomer || items.length === 0) {
      alert('Please select customer and add items');
      return;
    }

    const { subtotal, totalTax } = calculateTotals();
    let grandTotal = subtotal + totalTax;
    let roundOffAmount = 0;

    if (roundOff) {
      roundOffAmount = Math.round(grandTotal) - grandTotal;
      grandTotal = Math.round(grandTotal);
    }

    const invoiceData = {
      customerId: selectedCustomer,
      items,
      subtotal,
      totalTax,
      roundOff: roundOffAmount,
      grandTotal,
      paymentMode,
      invoiceState,
    };

    onSave(invoiceData);
  };

  const { subtotal, totalTax } = calculateTotals();
  let grandTotal = subtotal + totalTax;
  if (roundOff) {
    grandTotal = Math.round(grandTotal);
  }

  return (
    <div className="modal-overlay">
      <div className="modal invoice-modal-with-preview">
        <div className="modal-header">
          <h2>Create New Invoice</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="invoice-container">
          {/* LEFT SIDE - FORM */}
          <div className="invoice-form-section">
            <form onSubmit={handleSubmit} className="invoice-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Select Customer *</label>
                  <select value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} required>
                    <option value="">-- Choose Customer --</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onAddCustomer}
                  title="Add New Customer"
                >
                  + Add New
                </button>

                <div className="form-group">
                  <label>Payment Mode</label>
                  <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Card</option>
                    <option>Credit</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>State</label>
                  <select value={invoiceState} onChange={(e) => setInvoiceState(e.target.value)}>
                    <option>Karnataka</option>
                    <option>Tamil Nadu</option>
                    <option>Telangana</option>
                    <option>Andhra Pradesh</option>
                    <option>Maharashtra</option>
                    <option>Gujarat</option>
                    <option>Madhya Pradesh</option>
                    <option>Rajasthan</option>
                    <option>Uttar Pradesh</option>
                    <option>Delhi</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="add-item-section">
                <h3>Add Items</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product</label>
                    <select
                      value={currentItem.productId}
                      onChange={(e) => {
                        const product = products.find(p => p.id === e.target.value);
                        setCurrentItem({ 
                          ...currentItem, 
                          productId: e.target.value,
                          mrp: product?.mrp || product?.rate || 0
                        });
                      }}
                    >
                      <option value="">-- Select Product --</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.productName} - ₹{p.mrp || p.rate}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) })}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>MRP (Editable)</label>
                    <input
                      type="number"
                      value={currentItem.mrp}
                      onChange={(e) => setCurrentItem({ ...currentItem, mrp: parseFloat(e.target.value) })}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Discount %</label>
                    <input
                      type="number"
                      value={currentItem.discount}
                      onChange={(e) => setCurrentItem({ ...currentItem, discount: parseFloat(e.target.value) })}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <button type="button" className="btn btn-primary" onClick={handleAddItem}>
                    Add Item
                  </button>
                </div>
              </div>

              {items.length > 0 && (
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Rate</th>
                        <th>Taxable</th>
                        <th>Discount</th>
                        <th>Tax</th>
                        <th>MRP</th>
                        <th>Total</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const taxable = item.rate * item.quantity;
                        const discountAmt = (taxable * item.discount) / 100;
                        const finalTaxable = taxable - discountAmt;
                        const totalAmount = finalTaxable + item.cgst + item.sgst + item.igst;
                        
                        return (
                          <tr key={idx}>
                            <td>{item.productName}</td>
                            <td>{item.quantity}</td>
                            <td>₹{item.rate.toFixed(2)}</td>
                            <td>₹{item.rate.toFixed(2)}</td>
                            <td>{item.discount}%</td>
                            <td>{item.taxRate}%</td>
                            <td>₹{item.mrp.toFixed(2)}</td>
                            <td>₹{totalAmount.toFixed(2)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-icon delete"
                                onClick={() => handleRemoveItem(idx)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="invoice-summary">
                <div>
                  <label>Subtotal</label>
                  <p>₹{subtotal.toFixed(2)}</p>
                </div>
                <div>
                  <label>Total Tax</label>
                  <p>₹{totalTax.toFixed(2)}</p>
                </div>
                <div className="checkbox">
                  <input
                    type="checkbox"
                    id="roundOff"
                    checked={roundOff}
                    onChange={(e) => setRoundOff(e.target.checked)}
                  />
                  <label htmlFor="roundOff">Round-off</label>
                </div>
                <div className="grand-total">
                  <label>Grand Total</label>
                  <p>₹{grandTotal.toFixed(2)}</p>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Invoice
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT SIDE - INVOICE PREVIEW */}
          <div className="invoice-preview-section">
            <div className="invoice-preview">
              <h3>Invoice Preview</h3>
              
              {selectedCustomer && customers.find(c => c.id === selectedCustomer) && (
                <>
                  <div className="preview-header">
                    <h4>SSP BILLING SYSTEM</h4>
                    <p><strong>Customer:</strong> {customers.find(c => c.id === selectedCustomer)?.fullName}</p>
                    <p><strong>State:</strong> {invoiceState}</p>
                  </div>

                  {items.length > 0 ? (
                    <div className="preview-table">
                      <table className="invoice-table">
                        <thead>
                          <tr>
                            <th>SL</th>
                            <th>Product</th>
                            <th>HSN</th>
                            <th>State</th>
                            <th>Qty</th>
                            <th>Rate</th>
                            <th>MRP</th>
                            <th>Disc%</th>
                            <th>Taxable</th>
                            <th>Tax%</th>
                            <th>SGST</th>
                            <th>CGST</th>
                            <th>IGST</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => {
                            const taxableAmount = item.rate;
                            return (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="product-cell">{item.productName.substring(0, 15)}</td>
                                <td>{item.hsnCode?.substring(0, 8)}</td>
                                <td>{invoiceState?.substring(0, 3)}</td>
                                <td className="number">{item.quantity.toFixed(2)}</td>
                                <td className="number">₹{item.rate.toFixed(2)}</td>
                                <td className="number">₹{item.mrp.toFixed(2)}</td>
                                <td className="number">{item.discount.toFixed(1)}</td>
                                <td className="number">₹{taxableAmount.toFixed(2)}</td>
                                <td className="number">{item.taxRate}</td>
                                <td className="number">₹{item.sgst.toFixed(2)}</td>
                                <td className="number">₹{item.cgst.toFixed(2)}</td>
                                <td className="number">₹{item.igst.toFixed(2)}</td>
                                <td className="number"><strong>₹{item.itemTotal.toFixed(2)}</strong></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="preview-totals">
                        <div className="total-row">
                          <span>Subtotal:</span>
                          <strong>₹{subtotal.toFixed(2)}</strong>
                        </div>
                        <div className="total-row">
                          <span>Total Tax:</span>
                          <strong>₹{totalTax.toFixed(2)}</strong>
                        </div>
                        {roundOff && (
                          <div className="total-row">
                            <span>Round-off:</span>
                            <strong>₹{(Math.round(grandTotal) - (subtotal + totalTax)).toFixed(2)}</strong>
                          </div>
                        )}
                        <div className="total-row grand-total-row">
                          <span>GRAND TOTAL:</span>
                          <strong>₹{grandTotal.toFixed(2)}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="preview-empty">Add items to see invoice preview</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceForm;
